import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Heart, ChevronRight, ChevronLeft, Check, SkipForward, Sparkles, Loader2 } from 'lucide-react';
import type { OnboardingStep, OnboardingImage, OnboardingConfig } from '@/lib/api/extended-onboarding';
import { extendedOnboardingApi } from '@/lib/api/extended-onboarding';
import { cn } from '@/lib/utils';
import { resolveMediaUrl } from '@/lib/media';
import { VStack, HStack, Grid } from '@/components/core';

interface ExtendedOnboardingModalProps {
  open: boolean;
  onComplete: () => void;
  onSkip?: () => void;
}

/**
 * Extended Onboarding Modal for collecting user preferences.
 *
 * PERFORMANCE OPTIMIZED:
 * - Loads steps progressively (one at a time) instead of all at once
 * - Prefetches next step in background while user views current step
 * - Preloads images for smoother transitions
 *
 * Shows character images in gamified steps to identify user taste preferences.
 * Gender filter is automatically determined from user profile (gender + sexual orientation).
 */
export function ExtendedOnboardingModal({
  open,
  onComplete,
  onSkip
}: ExtendedOnboardingModalProps) {
  const { t, i18n } = useTranslation('common');
  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);
  const [nextStepCache, setNextStepCache] = useState<OnboardingStep | null>(null);
  const [config, setConfig] = useState<OnboardingConfig | null>(null);
  const [currentStepNumber, setCurrentStepNumber] = useState(1);
  const [selectedCharacters, setSelectedCharacters] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const prefetchedRef = useRef<Set<number>>(new Set());

  // Load config and first step
  useEffect(() => {
    if (!open) return;

    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        // Load config first (fast, small payload)
        const configData = await extendedOnboardingApi.getConfig();
        setConfig(configData);

        if (!configData.enabled) {
          onComplete();
          return;
        }

        // Load only the first step (much faster than all steps)
        const firstStep = await extendedOnboardingApi.getStep(1, i18n.language);
        setCurrentStep(firstStep);
        setCurrentStepNumber(1);

        // Preload images for first step
        preloadImages(firstStep.images);

        // Prefetch second step in background if there are multiple steps
        if (configData.steps > 1) {
          prefetchStep(2);
        }
      } catch (error) {
        console.error('Failed to load onboarding:', error);
        onComplete();
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [open, i18n.language, onComplete]);

  // Prefetch a step in the background
  const prefetchStep = useCallback(async (stepNumber: number) => {
    if (prefetchedRef.current.has(stepNumber)) return;

    try {
      prefetchedRef.current.add(stepNumber);
      const step = await extendedOnboardingApi.getStep(stepNumber, i18n.language);
      setNextStepCache(step);
      // Preload images for the next step
      preloadImages(step.images);
    } catch (error) {
      console.error(`Failed to prefetch step ${stepNumber}:`, error);
    }
  }, [i18n.language]);

  // Preload images for faster display
  const preloadImages = useCallback((images: OnboardingImage[]) => {
    images.forEach(img => {
      const image = new Image();
      image.src = resolveMediaUrl(img.image_url);
    });
  }, []);

  const totalSteps = config?.steps || 0;
  const isLastStep = currentStepNumber >= totalSteps;

  const handleImageSelect = useCallback((characterId: number) => {
    setSelectedCharacters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(characterId)) {
        newSet.delete(characterId);
      } else {
        newSet.add(characterId);
      }
      return newSet;
    });
  }, []);

  // Move to next step using cached data or fetch fresh
  const moveToNextStep = useCallback(async () => {
    const nextStepNumber = currentStepNumber + 1;

    if (nextStepCache && nextStepCache.step_number === nextStepNumber) {
      // Use cached step
      setCurrentStep(nextStepCache);
      setCurrentStepNumber(nextStepNumber);
      setNextStepCache(null);

      // Prefetch the step after next
      if (nextStepNumber < totalSteps) {
        prefetchStep(nextStepNumber + 1);
      }
    } else {
      // Fetch step directly (cache miss)
      setIsLoading(true);
      try {
        const step = await extendedOnboardingApi.getStep(nextStepNumber, i18n.language);
        setCurrentStep(step);
        setCurrentStepNumber(nextStepNumber);
        preloadImages(step.images);

        // Prefetch next
        if (nextStepNumber < totalSteps) {
          prefetchStep(nextStepNumber + 1);
        }
      } finally {
        setIsLoading(false);
      }
    }

    setSelectedCharacters(new Set());
  }, [currentStepNumber, nextStepCache, totalSteps, i18n.language, prefetchStep, preloadImages]);

  const handleNextStep = async () => {
    if (!currentStep) return;

    setIsSubmitting(true);
    try {
      // Get all shown character IDs from current step (to prevent duplicates in later steps)
      const shownCharacterIds = currentStep.images.map(img => img.character_id);

      // Save current selection
      await extendedOnboardingApi.saveSelection({
        step: currentStep.step_number,
        selected_character_ids: Array.from(selectedCharacters),
        shown_character_ids: shownCharacterIds,
        skipped: false
      });

      if (isLastStep) {
        // Complete onboarding
        await extendedOnboardingApi.complete();
        onComplete();
      } else {
        // Move to next step (use cache if available)
        await moveToNextStep();
      }
    } catch (error) {
      console.error('Failed to save selection:', error);
      toast({
        title: t('extendedOnboarding.error', 'Fehler'),
        description: t('extendedOnboarding.errorSaving', 'Auswahl konnte nicht gespeichert werden.'),
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipStep = async () => {
    if (!currentStep) return;

    setIsSubmitting(true);
    try {
      // Get all shown character IDs from current step (to prevent duplicates in later steps)
      const shownCharacterIds = currentStep.images.map(img => img.character_id);

      await extendedOnboardingApi.saveSelection({
        step: currentStep.step_number,
        selected_character_ids: [],
        shown_character_ids: shownCharacterIds,
        skipped: true
      });

      if (isLastStep) {
        await extendedOnboardingApi.complete();
        onComplete();
      } else {
        await moveToNextStep();
      }
    } catch (error) {
      console.error('Failed to skip step:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipAll = async () => {
    setIsSubmitting(true);
    try {
      await extendedOnboardingApi.skip();
      onSkip?.();
      onComplete();
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOnboarding = () => {
    setShowIntro(false);
  };

  if (!open) return null;

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/95" />
        <VStack align="center" className="relative z-10">
          <Loader2 className="w-12 h-12 text-brand-primary animate-spin" />
        </VStack>
      </div>
    );
  }

  // No step available
  if (!currentStep) {
    return null;
  }

  // Intro screen
  if (showIntro) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/95" />
        <div className="relative z-10 w-full max-w-md mx-token-sm bg-muted rounded-token-modal shadow-token-modal overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-secondary p-token-2xl text-center">
            <HStack justify="center" className="mb-token-md">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
            </HStack>
            <h2 className="text-2xl font-bold text-white mb-token-sm">
              {t('extendedOnboarding.introTitle', 'Finde deinen Typ')}
            </h2>
            <p className="text-white/80">
              {t('extendedOnboarding.introSubtitle', 'Hilf uns, deine perfekten Matches zu finden')}
            </p>
          </div>

          <div className="p-token-lg bg-muted space-y-token-md">
            <p className="text-muted-foreground text-center">
              {t('extendedOnboarding.introDescription', 'Wir zeigen dir ein paar Profile. Wähle die aus, die dir gefallen - so finden wir bessere Matches für dich!')}
            </p>

            <VStack gap="sm" className="pt-token-md">
              <Button
                onClick={handleStartOnboarding}
                className="w-full bg-gradient-to-r from-brand-primary to-brand-secondary hover:opacity-90"
              >
                <Heart className="w-4 h-4 mr-2" />
                {t('extendedOnboarding.startButton', "Los geht's!")}
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkipAll}
                disabled={isSubmitting}
                className="w-full text-muted hover:text-white"
              >
                <SkipForward className="w-4 h-4 mr-2" />
                {t('extendedOnboarding.skipButton', 'Überspringen')}
              </Button>
            </VStack>
          </div>
        </div>
      </div>
    );
  }

  // Main onboarding flow
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-token-md sm:py-token-2xl">
      <div className="absolute inset-0 bg-black/80" />

      <VStack className="relative z-10 w-full max-w-2xl mx-token-xs sm:mx-token-sm bg-muted rounded-token-modal shadow-token-modal overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)]">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-token-sm sm:p-token-md md:p-token-lg flex-shrink-0">
          <HStack justify="between" align="center" className="gap-token-sm">
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-white truncate">
                {currentStep?.title || t('extendedOnboarding.defaultTitle', 'Wähle deinen Typ')}
              </h2>
              <p className="text-white/80 text-xs sm:text-sm line-clamp-2">
                {currentStep?.description || t('extendedOnboarding.defaultDescription', 'Wähle die Profile, die dir gefallen')}
              </p>
            </div>
            <div className="text-white/80 text-xs sm:text-sm font-medium whitespace-nowrap bg-white/10 px-token-xs py-token-xs rounded-full">
              {currentStepNumber} / {totalSteps}
            </div>
          </HStack>

          {/* Progress bar */}
          <div className="mt-token-sm sm:mt-token-md h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${(currentStepNumber / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Image Grid - scrollable on mobile */}
        <div className="p-token-sm sm:p-token-md md:p-token-lg bg-muted flex-1 overflow-y-auto">
          <Grid cols={2} gap="sm" className="sm:gap-token-sm md:gap-token-md">
            {currentStep?.images.map((image) => (
              <ImageCard
                key={image.character_id}
                image={image}
                isSelected={selectedCharacters.has(image.character_id)}
                onSelect={() => handleImageSelect(image.character_id)}
              />
            ))}
          </Grid>
        </div>

        {/* Footer - fixed at bottom */}
        <div className="p-token-sm sm:p-token-md md:p-token-lg pt-token-xs sm:pt-token-sm bg-muted border-t border-muted-foreground/20 flex flex-col-reverse sm:flex-row gap-token-sm sm:gap-token-md flex-shrink-0">
          <Button
            variant="ghost"
            onClick={handleSkipStep}
            disabled={isSubmitting}
            className="sm:flex-1 text-muted hover:text-white text-sm"
          >
            <SkipForward className="w-4 h-4 mr-2" />
            {t('extendedOnboarding.skipStepButton', 'Überspringen')}
          </Button>

          <Button
            onClick={handleNextStep}
            disabled={isSubmitting}
            className="sm:flex-1 bg-gradient-to-r from-brand-primary to-brand-secondary hover:opacity-90 text-sm"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : isLastStep ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <ChevronRight className="w-4 h-4 mr-2" />
            )}
            {isLastStep
              ? t('extendedOnboarding.finishButton', 'Fertig')
              : t('extendedOnboarding.nextButton', 'Weiter')}
            {selectedCharacters.size > 0 && ` (${selectedCharacters.size})`}
          </Button>
        </div>
      </VStack>
    </div>
  );
}

/**
 * Individual image card component with selection state.
 */
interface ImageCardProps {
  image: OnboardingImage;
  isSelected: boolean;
  onSelect: () => void;
}

function ImageCard({ image, isSelected, onSelect }: ImageCardProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <button
      onClick={onSelect}
      className={cn(
        "relative aspect-[3/4] rounded-token-card overflow-hidden transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-muted",
        isSelected
          ? "ring-4 ring-brand-primary scale-[0.98] shadow-token-card shadow-brand-primary/30"
          : "hover:scale-[1.02] hover:shadow-token-card"
      )}
    >
      {/* Image */}
      {!imageError ? (
        <img
          src={resolveMediaUrl(image.image_url)}
          alt={image.character_name}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <Heart className="w-12 h-12 text-muted-foreground" />
        </div>
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

      {/* Character name */}
      <div className="absolute bottom-0 left-0 right-0 p-token-sm">
        <p className="text-white font-medium text-sm truncate">{image.character_name}</p>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center animate-in zoom-in duration-200">
          <Check className="w-5 h-5 text-white" />
        </div>
      )}

      {/* Hover overlay */}
      <div className={cn(
        "absolute inset-0 transition-opacity duration-200",
        isSelected ? "bg-brand-primary/10" : "bg-transparent hover:bg-white/5"
      )} />
    </button>
  );
}

export default ExtendedOnboardingModal;
